'use client';

import { useState, useEffect } from 'react';
import { useSearchParams } from 'next/navigation';

import Icon from '@/components/ui/AppIcon';
import ServiceCard from './ServiceCard';
import PropertyTypeSelector from './PropertyTypeSelector';
import FrequencySelector from './FrequencySelector';
import ProgressIndicator from './ProgressIndicator';
import QuoteSummary from './QuoteSummary';
import { quoteRequestService } from '@/services/quoteRequestService';



interface Service {
  id: string;
  icon: string;
  title: string;
  description: string;
  basePrice: number;
}

interface PropertyType {
  id: string;
  label: string;
  icon: string;
}

interface Frequency {
  id: string;
  label: string;
  description: string;
  multiplier: number;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
  preferredDate: string;
  preferredTime: string;
  additionalNotes: string;
}

export default function QuoteRequestInteractive() {
  const searchParams = useSearchParams();
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedService, setSelectedService] = useState<string>('');
  const [selectedPropertyType, setSelectedPropertyType] = useState<string>('');
  const [selectedFrequency, setSelectedFrequency] = useState<string>('');
  const [surfaceArea, setSurfaceArea] = useState<number>(100);
  const [rooms, setRooms] = useState<number>(3);
  const [bathrooms, setBathrooms] = useState<number>(1);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: 'Le Locle',
    postalCode: '',
    preferredDate: '',
    preferredTime: '',
    additionalNotes: ''
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [quoteNumber, setQuoteNumber] = useState<string>('');
  const [submissionMethod, setSubmissionMethod] = useState<'email' | 'whatsapp' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  useEffect(() => {
    setIsHydrated(true);
    
    // Pre-select service from URL parameter
    const serviceParam = searchParams.get('service');
    if (serviceParam) {
      // Map service titles to service IDs
      const serviceMapping: { [key: string]: string } = {
        'Nettoyage Résidentiel Complet': 'regular-cleaning',
        'Nettoyage de Bureaux': 'office-cleaning',
        'Nettoyage Après Construction': 'deep-cleaning',
        'Services de Conciergerie': 'regular-cleaning',
        'Nettoyage de Vitres': 'window-cleaning',
        'Nettoyage de Tapis et Moquettes': 'carpet-cleaning',
        'Jardinage et Entretien Extérieur': 'regular-cleaning',
        'Petits Travaux de Bricolage': 'regular-cleaning'
      };
      
      const serviceId = serviceMapping[serviceParam];
      if (serviceId) {
        setSelectedService(serviceId);
      }
    }
  }, [searchParams]);

  const services: Service[] = [
    {
      id: 'regular-cleaning',
      icon: '🧹',
      title: 'Nettoyage régulier',
      description: 'Entretien hebdomadaire ou bimensuel de votre domicile',
      basePrice: 45
    },
    {
      id: 'deep-cleaning',
      icon: '✨',
      title: 'Nettoyage en profondeur',
      description: 'Nettoyage complet et détaillé de tous les espaces',
      basePrice: 75
    },
    {
      id: 'move-cleaning',
      icon: '📦',
      title: 'Nettoyage de déménagement',
      description: 'Nettoyage avant ou après déménagement',
      basePrice: 90
    },
    {
      id: 'office-cleaning',
      icon: '🏢',
      title: 'Nettoyage de bureaux',
      description: 'Entretien professionnel de vos locaux commerciaux',
      basePrice: 55
    },
    {
      id: 'window-cleaning',
      icon: '🪟',
      title: 'Nettoyage de vitres',
      description: 'Nettoyage intérieur et extérieur des fenêtres',
      basePrice: 35
    },
    {
      id: 'carpet-cleaning',
      icon: '🛋️',
      title: 'Nettoyage de tapis',
      description: 'Nettoyage professionnel de tapis et moquettes',
      basePrice: 50
    }
  ];

  const propertyTypes: PropertyType[] = [
    { id: 'apartment', label: 'Appartement', icon: '🏢' },
    { id: 'house', label: 'Maison', icon: '🏠' },
    { id: 'office', label: 'Bureau', icon: '🏪' },
    { id: 'commercial', label: 'Commercial', icon: '🏬' }
  ];

  const frequencies: Frequency[] = [
    {
      id: 'once',
      label: 'Une fois',
      description: 'Service ponctuel unique',
      multiplier: 1
    },
    {
      id: 'weekly',
      label: 'Hebdomadaire',
      description: 'Chaque semaine - Économisez 15%',
      multiplier: 0.85
    },
    {
      id: 'biweekly',
      label: 'Bimensuel',
      description: 'Toutes les deux semaines - Économisez 10%',
      multiplier: 0.90
    },
    {
      id: 'monthly',
      label: 'Mensuel',
      description: 'Une fois par mois - Économisez 5%',
      multiplier: 0.95
    }
  ];

  const steps = [
    { number: 1, label: 'Service' },
    { number: 2, label: 'Détails' },
    { number: 3, label: 'Coordonnées' },
    { number: 4, label: 'Confirmation' }
  ];

  const calculatePrice = (): number => {
    if (!selectedService || !selectedFrequency) return 0;

    const service = services.find(s => s.id === selectedService);
    const frequency = frequencies.find(f => f.id === selectedFrequency);
    
    if (!service || !frequency) return 0;

    let basePrice = service.basePrice;
    
    // Surface area factor
    const surfaceFactor = surfaceArea / 100;
    basePrice *= surfaceFactor;
    
    // Rooms factor
    const roomsFactor = 1 + (rooms - 3) * 0.1;
    basePrice *= roomsFactor;
    
    // Bathrooms factor
    const bathroomsFactor = 1 + (bathrooms - 1) * 0.15;
    basePrice *= bathroomsFactor;
    
    // Apply frequency multiplier
    const totalPrice = basePrice * frequency.multiplier;
    
    return Math.round(totalPrice * 100) / 100;
  };

  const handleNext = () => {
    if (currentStep === 1 && !selectedService) {
      setAlertMessage('Veuillez sélectionner un service');
      return;
    }
    if (currentStep === 2 && (!selectedPropertyType || !selectedFrequency)) {
      setAlertMessage('Veuillez compléter tous les détails');
      return;
    }
    if (currentStep === 3) {
      if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
        setAlertMessage('Veuillez remplir tous les champs obligatoires');
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setAlertMessage('Veuillez entrer une adresse email valide');
        return;
      }
    }
    
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrorMessage(null);
    setErrorDetails(null);
    
    const randomQuoteNumber = `RQ-${new Date().getFullYear()}-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;
    setQuoteNumber(randomQuoteNumber);
    
    const service = services.find(s => s.id === selectedService);
    const propertyType = propertyTypes.find(p => p.id === selectedPropertyType);
    const frequency = frequencies.find(f => f.id === selectedFrequency);
    const estimatedPrice = calculatePrice();
    
    // Save to Supabase database only
    const quoteData = {
      quoteNumber: randomQuoteNumber,
      serviceId: selectedService,
      serviceName: service?.title || '',
      propertyType: propertyType?.label || '',
      frequency: frequency?.label || '',
      surfaceArea: surfaceArea,
      rooms: rooms,
      bathrooms: bathrooms,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      address: formData.address,
      city: formData.city,
      postalCode: formData.postalCode,
      preferredDate: formData.preferredDate,
      preferredTime: formData.preferredTime,
      additionalNotes: formData.additionalNotes,
      basePrice: getBasePrice(),
      frequencyMultiplier: getFrequencyMultiplier(),
      estimatedPrice: estimatedPrice,
      submissionMethod: 'direct' as const
    };

    // Save to database
    const { data: savedQuote, error: dbError } = await quoteRequestService.create(quoteData);
    
    if (dbError) {
      setErrorMessage('Erreur de sauvegarde');
      setErrorDetails(dbError.message);
      setShowErrorModal(true);
      setIsSubmitting(false);
      return;
    }
    
    // Show success confirmation immediately after saving
    setSubmissionMethod('direct');
    setShowConfirmation(true);
    setIsSubmitting(false);
  };

  const handleWhatsAppSubmit = async () => {
    const randomQuoteNumber = `RQ-${new Date().getFullYear()}-${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;
    setQuoteNumber(randomQuoteNumber);
    
    const service = services.find(s => s.id === selectedService);
    const propertyType = propertyTypes.find(p => p.id === selectedPropertyType);
    const frequency = frequencies.find(f => f.id === selectedFrequency);
    const estimatedPrice = calculatePrice();
    
    // Save to Supabase database
    const quoteData = {
      quoteNumber: randomQuoteNumber,
      serviceId: selectedService,
      serviceName: service?.title || '',
      propertyType: propertyType?.label || '',
      frequency: frequency?.label || '',
      surfaceArea: surfaceArea,
      rooms: rooms,
      bathrooms: bathrooms,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone,
      address: formData.address,
      city: formData.city,
      postalCode: formData.postalCode,
      preferredDate: formData.preferredDate,
      preferredTime: formData.preferredTime,
      additionalNotes: formData.additionalNotes,
      basePrice: getBasePrice(),
      frequencyMultiplier: getFrequencyMultiplier(),
      estimatedPrice: estimatedPrice,
      submissionMethod: 'whatsapp' as const
    };

    // Save to database
    const { data: savedQuote, error: dbError } = await quoteRequestService.create(quoteData);
    
    if (dbError) {
      alert(`Erreur lors de la sauvegarde: ${dbError.message}`);
      return;
    }
    
    // Prepare WhatsApp message
    const whatsappMessage = `Bonjour Rouissi Services,

Je souhaite obtenir un devis pour les services suivants :

═══════════════════════════════════════
INFORMATIONS DU SERVICE
═══════════════════════════════════════

Numéro de devis : ${randomQuoteNumber}
Service demandé : ${service?.title || ''}
Type de propriété : ${propertyType?.label || ''}
Fréquence : ${frequency?.label || ''}

Détails de la propriété :
- Surface : ${surfaceArea} m²
- Nombre de pièces : ${rooms}
- Nombre de salles de bain : ${bathrooms}

═══════════════════════════════════════
COORDONNÉES CLIENT
═══════════════════════════════════════

Nom complet : ${formData.firstName} ${formData.lastName}
Email : ${formData.email}
Téléphone : ${formData.phone}
${formData.address ? `Adresse : ${formData.address}` : ''}
${formData.city ? `Ville : ${formData.city}` : ''}
${formData.postalCode ? `Code postal : ${formData.postalCode}` : ''}

═══════════════════════════════════════
PRÉFÉRENCES
═══════════════════════════════════════

${formData.preferredDate ? `Date préférée : ${formData.preferredDate}` : ''}
${formData.preferredTime ? `Heure préférée : ${formData.preferredTime === 'morning' ? 'Matin (8h-12h)' : formData.preferredTime === 'afternoon' ? 'Après-midi (12h-17h)' : formData.preferredTime === 'evening' ? 'Soir (17h-20h)' : ''}` : ''}

${formData.additionalNotes ? `Notes supplémentaires :\n${formData.additionalNotes}` : ''}

═══════════════════════════════════════
ESTIMATION DU PRIX
═══════════════════════════════════════

Prix de base : CHF ${getBasePrice().toFixed(2)}
Multiplicateur de fréquence : ${(getFrequencyMultiplier() * 100).toFixed(0)}%
Prix total estimé : CHF ${calculatePrice().toFixed(2)}

═══════════════════════════════════════

Cordialement,
${formData.firstName} ${formData.lastName}`;
    
    // Open WhatsApp with pre-filled message
    const whatsappUrl = `https://wa.me/41762126506?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(whatsappUrl, '_blank');
    
    setSubmissionMethod('whatsapp');
    setShowConfirmation(true);
  };

  const handleNewQuote = () => {
    setCurrentStep(1);
    setSelectedService('');
    setSelectedPropertyType('');
    setSelectedFrequency('');
    setSurfaceArea(100);
    setRooms(3);
    setBathrooms(1);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: 'Le Locle',
      postalCode: '',
      preferredDate: '',
      preferredTime: '',
      additionalNotes: ''
    });
    setShowConfirmation(false);
    setQuoteNumber('');
  };

  const getServiceName = (): string => {
    const service = services.find(s => s.id === selectedService);
    return service ? service.title : '';
  };

  const getPropertyTypeName = (): string => {
    const type = propertyTypes.find(p => p.id === selectedPropertyType);
    return type ? type.label : '';
  };

  const getFrequencyName = (): string => {
    const freq = frequencies.find(f => f.id === selectedFrequency);
    return freq ? freq.label : '';
  };

  const getBasePrice = (): number => {
    const service = services.find(s => s.id === selectedService);
    if (!service) return 0;
    
    let basePrice = service.basePrice;
    const surfaceFactor = surfaceArea / 100;
    basePrice *= surfaceFactor;
    const roomsFactor = 1 + (rooms - 3) * 0.1;
    basePrice *= roomsFactor;
    const bathroomsFactor = 1 + (bathrooms - 1) * 0.15;
    basePrice *= bathroomsFactor;
    
    return Math.round(basePrice * 100) / 100;
  };

  const getFrequencyMultiplier = (): number => {
    const freq = frequencies.find(f => f.id === selectedFrequency);
    return freq ? freq.multiplier : 1;
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background pt-20 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="animate-pulse">
              <div className="h-12 bg-surface rounded w-3/4 mb-4"></div>
              <div className="h-6 bg-surface rounded w-1/2 mb-8"></div>
              <div className="h-64 bg-surface rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showConfirmation) {
    const service = services.find(s => s.id === selectedService);
    const propertyType = propertyTypes.find(p => p.id === selectedPropertyType);
    const frequency = frequencies.find(f => f.id === selectedFrequency);
    const estimatedPrice = calculatePrice();
    
    return (
      <div className="min-h-screen bg-background pt-20 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Success Header */}
            <div className="bg-card rounded-lg shadow-lg p-8 md:p-12 text-center mb-6">
              <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="CheckCircleIcon" size={48} variant="solid" className="text-success" />
              </div>
              
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
                Demande de devis envoyée !
              </h1>
              
              <p className="text-lg text-muted-foreground mb-6">
                Merci pour votre demande. Voici le récapitulatif complet de votre devis.
              </p>
              
              <div className="bg-surface rounded-lg p-6">
                <div className="flex items-center justify-center space-x-2 mb-4">
                  <Icon name="DocumentTextIcon" size={24} variant="outline" className="text-primary" />
                  <span className="text-sm text-muted-foreground">Numéro de devis</span>
                </div>
                <p className="text-2xl font-heading font-bold text-primary">{quoteNumber}</p>
              </div>
            </div>

            {/* Quote Summary */}
            <div className="bg-card rounded-lg shadow-lg p-8 mb-6">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6 flex items-center">
                <Icon name="ClipboardDocumentListIcon" size={28} variant="outline" className="text-primary mr-3" />
                Récapitulatif du devis
              </h2>
              
              {/* Service Details */}
              <div className="space-y-4 mb-6">
                <div className="border-b border-border pb-4">
                  <h3 className="text-sm font-heading font-semibold text-muted-foreground mb-3">DÉTAILS DU SERVICE</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Service demandé</p>
                      <p className="text-base font-medium text-foreground">{service?.title}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Type de propriété</p>
                      <p className="text-base font-medium text-foreground">{propertyType?.label}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Fréquence</p>
                      <p className="text-base font-medium text-foreground">{frequency?.label}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Surface</p>
                      <p className="text-base font-medium text-foreground">{surfaceArea} m²</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Nombre de pièces</p>
                      <p className="text-base font-medium text-foreground">{rooms}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Salles de bain</p>
                      <p className="text-base font-medium text-foreground">{bathrooms}</p>
                    </div>
                  </div>
                </div>

                {/* Client Information */}
                <div className="border-b border-border pb-4">
                  <h3 className="text-sm font-heading font-semibold text-muted-foreground mb-3">VOS COORDONNÉES</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Nom complet</p>
                      <p className="text-base font-medium text-foreground">{formData.firstName} {formData.lastName}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <p className="text-base font-medium text-foreground">{formData.email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Téléphone</p>
                      <p className="text-base font-medium text-foreground">{formData.phone}</p>
                    </div>
                    {formData.address && (
                      <div>
                        <p className="text-sm text-muted-foreground">Adresse</p>
                        <p className="text-base font-medium text-foreground">{formData.address}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-sm text-muted-foreground">Ville</p>
                      <p className="text-base font-medium text-foreground">{formData.city}</p>
                    </div>
                    {formData.postalCode && (
                      <div>
                        <p className="text-sm text-muted-foreground">Code postal</p>
                        <p className="text-base font-medium text-foreground">{formData.postalCode}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Preferences */}
                {(formData.preferredDate || formData.preferredTime || formData.additionalNotes) && (
                  <div className="border-b border-border pb-4">
                    <h3 className="text-sm font-heading font-semibold text-muted-foreground mb-3">PRÉFÉRENCES</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {formData.preferredDate && (
                        <div>
                          <p className="text-sm text-muted-foreground">Date préférée</p>
                          <p className="text-base font-medium text-foreground">{formData.preferredDate}</p>
                        </div>
                      )}
                      {formData.preferredTime && (
                        <div>
                          <p className="text-sm text-muted-foreground">Heure préférée</p>
                          <p className="text-base font-medium text-foreground">
                            {formData.preferredTime === 'morning' ? 'Matin (8h-12h)' : 
                             formData.preferredTime === 'afternoon' ? 'Après-midi (12h-17h)' : 
                             formData.preferredTime === 'evening' ? 'Soir (17h-20h)' : ''}
                          </p>
                        </div>
                      )}
                    </div>
                    {formData.additionalNotes && (
                      <div className="mt-4">
                        <p className="text-sm text-muted-foreground">Notes supplémentaires</p>
                        <p className="text-base font-medium text-foreground">{formData.additionalNotes}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Price Estimate */}
                <div className="bg-primary/5 rounded-lg p-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Prix estimé</p>
                      <p className="text-xs text-muted-foreground">(Tarif indicatif, devis final après évaluation)</p>
                    </div>
                    <p className="text-3xl font-heading font-bold text-primary">CHF {estimatedPrice.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Instructions */}
            <div className="bg-card rounded-lg shadow-lg p-8 mb-6">
              <h2 className="text-2xl font-heading font-bold text-foreground mb-6 flex items-center">
                <Icon name="ChatBubbleLeftRightIcon" size={28} variant="outline" className="text-primary mr-3" />
                Prochaines étapes
              </h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-primary font-bold">1</span>
                  </div>
                  <div>
                    <p className="text-base font-heading font-medium text-foreground">Confirmation envoyée</p>
                    <p className="text-sm text-muted-foreground">
                      Votre demande a été transmise à notre équipe
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-primary font-bold">2</span>
                  </div>
                  <div>
                    <p className="text-base font-heading font-medium text-foreground">Réponse sous 24h</p>
                    <p className="text-sm text-muted-foreground">
                      Nous vous contacterons rapidement pour finaliser votre devis personnalisé
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-primary font-bold">3</span>
                  </div>
                  <div>
                    <p className="text-base font-heading font-medium text-foreground">Besoin d'une réponse immédiate ?</p>
                    <p className="text-sm text-muted-foreground mb-3">
                      Contactez-nous directement par email ou WhatsApp
                    </p>
                    
                    {/* Direct Contact Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3">
                      <a
                        href={`mailto:servicesrouissi@gmail.com?subject=Devis ${quoteNumber} - ${service?.title}&body=Bonjour,%0D%0A%0D%0AJe souhaite obtenir plus d'informations concernant ma demande de devis :%0D%0A%0D%0ANuméro de devis : ${quoteNumber}%0D%0AService : ${service?.title}%0D%0ANom : ${formData.firstName} ${formData.lastName}%0D%0ATéléphone : ${formData.phone}%0D%0A%0D%0ACordialement`}
                        className="inline-flex items-center justify-center px-6 py-3 bg-primary text-primary-foreground rounded-lg font-heading font-semibold hover:bg-primary/90 transition-colors duration-300"
                      >
                        <Icon name="EnvelopeIcon" size={20} variant="solid" className="mr-2" />
                        Envoyer un email
                      </a>
                      
                      <a
                        href={`https://wa.me/41762126506?text=${encodeURIComponent(`Bonjour Rouissi Services,\n\nJe souhaite obtenir plus d'informations sur ma demande de devis :\n\nNuméro de devis : ${quoteNumber}\nService : ${service?.title}\nNom : ${formData.firstName} ${formData.lastName}\nTéléphone : ${formData.phone}\n\nCordialement`)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center px-6 py-3 bg-[#25D366] text-white rounded-lg font-heading font-semibold hover:bg-[#20BA5A] transition-colors duration-300"
                      >
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                        Contacter sur WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-surface rounded-lg p-6">
                <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Coordonnées de contact</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Icon name="EnvelopeIcon" size={20} variant="outline" className="text-primary" />
                    <a href="mailto:servicesrouissi@gmail.com" className="text-sm text-foreground hover:text-primary transition-colors">
                      servicesrouissi@gmail.com
                    </a>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Icon name="PhoneIcon" size={20} variant="outline" className="text-primary" />
                    <a href="tel:+41762126506" className="text-sm text-foreground hover:text-primary transition-colors">
                      +41 76 212 65 06
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleNewQuote}
                className="px-8 py-3 bg-surface text-foreground rounded-lg font-heading font-semibold hover:bg-muted transition-colors duration-300"
              >
                Nouvelle demande
              </button>
              <a
                href="/homepage"
                className="px-8 py-3 bg-primary text-primary-foreground rounded-lg font-heading font-semibold hover:bg-primary/90 transition-colors duration-300 text-center"
              >
                Retour à l'accueil
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20 pb-16">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
              Demande de devis gratuit
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Obtenez une estimation instantanée en CHF pour vos services de nettoyage. Réponse garantie sous 24h.
            </p>
          </div>

          {/* Progress Indicator */}
          <div className="mb-12">
            <ProgressIndicator steps={steps} currentStep={currentStep} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Form Area */}
            <div className="lg:col-span-2">
              <div className="bg-card rounded-lg shadow-lg p-6 md:p-8">
                {/* Step 1: Service Selection */}
                {currentStep === 1 && (
                  <div>
                    <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                      Sélectionnez votre service
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      Choisissez le type de service dont vous avez besoin
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {services.map((service) => (
                        <ServiceCard
                          key={service.id}
                          icon={service.icon}
                          title={service.title}
                          description={service.description}
                          isSelected={selectedService === service.id}
                          onClick={() => setSelectedService(service.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 2: Property Details */}
                {currentStep === 2 && (
                  <div>
                    <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                      Détails de la propriété
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      Fournissez les informations sur votre propriété
                    </p>
                    
                    <div className="space-y-8">
                      {/* Property Type */}
                      <div>
                        <label className="block text-sm font-heading font-semibold text-foreground mb-3">
                          Type de propriété *
                        </label>
                        <PropertyTypeSelector
                          propertyTypes={propertyTypes}
                          selectedType={selectedPropertyType}
                          onSelect={setSelectedPropertyType}
                        />
                      </div>

                      {/* Surface Area */}
                      <div>
                        <label className="block text-sm font-heading font-semibold text-foreground mb-3">
                          Surface (m²) *
                        </label>
                        <div className="flex items-center space-x-4">
                          <input
                            type="range"
                            min="30"
                            max="500"
                            step="10"
                            value={surfaceArea}
                            onChange={(e) => setSurfaceArea(Number(e.target.value))}
                            className="flex-1 h-2 bg-surface rounded-lg appearance-none cursor-pointer accent-primary"
                          />
                          <div className="w-20 px-3 py-2 bg-surface rounded-lg text-center">
                            <span className="text-sm font-heading font-semibold text-foreground">
                              {surfaceArea} m²
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Rooms */}
                      <div>
                        <label className="block text-sm font-heading font-semibold text-foreground mb-3">
                          Nombre de pièces *
                        </label>
                        <div className="flex items-center space-x-4">
                          <button
                            onClick={() => setRooms(Math.max(1, rooms - 1))}
                            className="w-12 h-12 bg-surface rounded-lg flex items-center justify-center hover:bg-muted transition-colors duration-300 active:scale-95"
                          >
                            <Icon name="MinusIcon" size={24} variant="outline" className="text-foreground" />
                          </button>
                          <div className="flex-1 px-4 py-3 bg-surface rounded-lg text-center">
                            <span className="text-lg font-heading font-semibold text-foreground">{rooms}</span>
                          </div>
                          <button
                            onClick={() => setRooms(Math.min(20, rooms + 1))}
                            className="w-12 h-12 bg-surface rounded-lg flex items-center justify-center hover:bg-muted transition-colors duration-300 active:scale-95"
                          >
                            <Icon name="PlusIcon" size={24} variant="outline" className="text-foreground" />
                          </button>
                        </div>
                      </div>

                      {/* Bathrooms */}
                      <div>
                        <label className="block text-sm font-heading font-semibold text-foreground mb-3">
                          Nombre de salles de bain *
                        </label>
                        <div className="flex items-center space-x-4">
                          <button
                            onClick={() => setBathrooms(Math.max(1, bathrooms - 1))}
                            className="w-12 h-12 bg-surface rounded-lg flex items-center justify-center hover:bg-muted transition-colors duration-300 active:scale-95"
                          >
                            <Icon name="MinusIcon" size={24} variant="outline" className="text-foreground" />
                          </button>
                          <div className="flex-1 px-4 py-3 bg-surface rounded-lg text-center">
                            <span className="text-lg font-heading font-semibold text-foreground">{bathrooms}</span>
                          </div>
                          <button
                            onClick={() => setBathrooms(Math.min(10, bathrooms + 1))}
                            className="w-12 h-12 bg-surface rounded-lg flex items-center justify-center hover:bg-muted transition-colors duration-300 active:scale-95"
                          >
                            <Icon name="PlusIcon" size={24} variant="outline" className="text-foreground" />
                          </button>
                        </div>
                      </div>

                      {/* Frequency */}
                      <div>
                        <label className="block text-sm font-heading font-semibold text-foreground mb-3">
                          Fréquence du service *
                        </label>
                        <FrequencySelector
                          frequencies={frequencies}
                          selectedFrequency={selectedFrequency}
                          onSelect={setSelectedFrequency}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 3: Contact Information */}
                {currentStep === 3 && (
                  <div>
                    <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                      Vos coordonnées
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      Nous avons besoin de ces informations pour vous contacter
                    </p>
                    
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-heading font-semibold text-foreground mb-2">
                            Prénom *
                          </label>
                          <input
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                            className="w-full px-4 py-3.5 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-base"
                            placeholder="Jean"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-heading font-semibold text-foreground mb-2">
                            Nom *
                          </label>
                          <input
                            type="text"
                            value={formData.lastName}
                            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                            className="w-full px-4 py-3.5 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-base"
                            placeholder="Dupont"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-heading font-semibold text-foreground mb-2">
                            Email *
                          </label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            className="w-full px-4 py-3.5 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-base"
                            placeholder="jean.dupont@example.com"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-heading font-semibold text-foreground mb-2">
                            Téléphone *
                          </label>
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            className="w-full px-4 py-3.5 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-base"
                            placeholder="+41 32 123 45 67"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-heading font-semibold text-foreground mb-2">
                          Adresse
                        </label>
                        <input
                          type="text"
                          value={formData.address}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                          className="w-full px-4 py-3.5 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-base"
                          placeholder="Rue de la Gare 15"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-heading font-semibold text-foreground mb-2">
                            Ville
                          </label>
                          <input
                            type="text"
                            value={formData.city}
                            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                            className="w-full px-4 py-3.5 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-base"
                            placeholder="Le Locle"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-heading font-semibold text-foreground mb-2">
                            Code postal
                          </label>
                          <input
                            type="text"
                            value={formData.postalCode}
                            onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                            className="w-full px-4 py-3.5 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-base"
                            placeholder="2400"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-heading font-semibold text-foreground mb-2">
                            Date préférée
                          </label>
                          <input
                            type="date"
                            value={formData.preferredDate}
                            onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
                            className="w-full px-4 py-3.5 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-base"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-heading font-semibold text-foreground mb-2">
                            Heure préférée
                          </label>
                          <select
                            value={formData.preferredTime}
                            onChange={(e) => setFormData({ ...formData, preferredTime: e.target.value })}
                            className="w-full px-4 py-3.5 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground text-base"
                          >
                            <option value="">Sélectionner</option>
                            <option value="morning">Matin (8h-12h)</option>
                            <option value="afternoon">Après-midi (12h-17h)</option>
                            <option value="evening">Soir (17h-20h)</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-heading font-semibold text-foreground mb-2">
                          Notes supplémentaires
                        </label>
                        <textarea
                          value={formData.additionalNotes}
                          onChange={(e) => setFormData({ ...formData, additionalNotes: e.target.value })}
                          rows={4}
                          className="w-full px-4 py-3.5 bg-surface border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-foreground resize-none text-base"
                          placeholder="Informations supplémentaires sur vos besoins..."
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
                  {currentStep > 1 ? (
                    <button
                      onClick={handleBack}
                      disabled={isSubmitting}
                      className="flex items-center space-x-2 px-6 py-3 bg-surface text-foreground rounded-lg font-heading font-semibold hover:bg-muted transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Icon name="ArrowLeftIcon" size={20} variant="outline" />
                      <span>Retour</span>
                    </button>
                  ) : (
                    <div></div>
                  )}
                  
                  {currentStep === 3 ? (
                    <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-3">
                      <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="w-full sm:w-auto flex items-center justify-center space-x-2 px-6 py-3.5 bg-primary text-primary-foreground rounded-lg font-heading font-semibold hover:bg-primary/90 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]"
                      >
                        {isSubmitting ? (
                          <>
                            <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Sauvegarde...</span>
                          </>
                        ) : (
                          <>
                            <Icon name="CheckCircleIcon" size={20} variant="solid" />
                            <span>Soumettre le devis</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={handleWhatsAppSubmit}
                        disabled={isSubmitting}
                        className="w-full sm:w-auto flex items-center justify-center space-x-2 px-6 py-3.5 bg-[#25D366] text-white rounded-lg font-heading font-semibold hover:bg-[#20BA5A] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                        <span>Envoyer via WhatsApp</span>
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleNext}
                      disabled={isSubmitting}
                      className="flex items-center space-x-2 px-6 py-3.5 bg-primary text-primary-foreground rounded-lg font-heading font-semibold hover:bg-primary/90 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed min-h-[48px]"
                    >
                      <span>Suivant</span>
                      <Icon name="ArrowRightIcon" size={20} variant="outline" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Quote Summary Sidebar */}
            <div className="lg:col-span-1">
              <div className="lg:sticky lg:top-24">
                {selectedService && selectedFrequency && (
                  <QuoteSummary
                    serviceName={getServiceName()}
                    propertyType={getPropertyTypeName()}
                    frequency={getFrequencyName()}
                    surfaceArea={surfaceArea}
                    rooms={rooms}
                    bathrooms={bathrooms}
                    basePrice={getBasePrice()}
                    frequencyMultiplier={getFrequencyMultiplier()}
                    totalPrice={calculatePrice()}
                  />
                )}

                {/* Trust Signals */}
                <div className="mt-6 bg-card rounded-lg p-6 border border-border">
                  <h4 className="text-lg font-heading font-bold text-foreground mb-4">
                    Pourquoi nous choisir ?
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-success mt-0.5" />
                      <div>
                        <p className="text-sm font-heading font-medium text-foreground">Devis gratuit</p>
                        <p className="text-xs text-muted-foreground">Sans engagement</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <Icon name="ClockIcon" size={20} variant="solid" className="text-success mt-0.5" />
                      <div>
                        <p className="text-sm font-heading font-medium text-foreground">Réponse rapide</p>
                        <p className="text-xs text-muted-foreground">Sous 24 heures</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <Icon name="ShieldCheckIcon" size={20} variant="solid" className="text-success mt-0.5" />
                      <div>
                        <p className="text-sm font-heading font-medium text-foreground">Service assuré</p>
                        <p className="text-xs text-muted-foreground">Qualité garantie</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <Icon name="StarIcon" size={20} variant="solid" className="text-success mt-0.5" />
                      <div>
                        <p className="text-sm font-heading font-medium text-foreground">Satisfaction client</p>
                        <p className="text-xs text-muted-foreground">4.9/5 étoiles</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Alert Toast */}
          {alertMessage && (
            <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-md px-4 animate-fade-in">
              <div className="bg-card border-2 border-destructive rounded-lg shadow-xl p-4 flex items-start space-x-3">
                <Icon name="ExclamationTriangleIcon" size={24} variant="solid" className="text-destructive flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{alertMessage}</p>
                </div>
                <button
                  onClick={() => setAlertMessage(null)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name="XMarkIcon" size={20} variant="outline" />
                </button>
              </div>
            </div>
          )}
          
          {/* Error Modal */}
          {showErrorModal && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-card rounded-lg shadow-xl max-w-md w-full p-6">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="ExclamationTriangleIcon" size={24} variant="outline" className="text-destructive" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-heading font-bold text-foreground mb-2">
                      {errorMessage}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {errorDetails}
                    </p>
                    
                    {errorMessage?.includes('clé API') && (
                      <div className="bg-surface rounded-lg p-4 mb-4">
                        <p className="text-xs text-muted-foreground mb-2">
                          <strong>Solution alternative :</strong>
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Votre demande a été sauvegardée avec le numéro <strong>{quoteNumber}</strong>. Vous pouvez nous contacter directement par WhatsApp ou téléphone pour finaliser votre demande.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-col space-y-3">
                  <button
                    onClick={() => {
                      setShowErrorModal(false);
                      setErrorMessage(null);
                      setErrorDetails(null);
                    }}
                    className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg font-heading font-semibold hover:bg-primary/90 transition-colors duration-300"
                  >
                    Fermer
                  </button>
                  
                  {errorMessage?.includes('clé API') && (
                    <a
                      href={`https://wa.me/41762126506?text=${encodeURIComponent(`Bonjour, j'ai rencontré un problème lors de l'envoi de ma demande de devis (${quoteNumber}). Pouvez-vous m'aider ?`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full px-6 py-3 bg-[#25D366] text-white rounded-lg font-heading font-semibold hover:bg-[#20BA5A] transition-colors duration-300 text-center"
                    >
                      Contacter via WhatsApp
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}